import { useCallback, useEffect, useRef, useState } from "react";

import { apiFetch } from "@/lib/api-client";
import { Skeleton, SkeletonCard } from "@/components/skeleton";

const REFRESH_INTERVAL_MS = 30_000;

interface WorkerHealth {
  name: string;
  url: string;
  status: "healthy" | "down" | "unknown";
  lastCheckAt: string | null;
  latencyMs: number;
  httpStatus: number | null;
  error: string | null;
}

interface HealthResponse {
  summary: {
    totalWorkers: number;
    healthyCount: number;
    errorRate1h: number;
    p95LatencyMs: number;
  };
  workers: WorkerHealth[];
}

type IncidentStatus = "investigating" | "identified" | "monitoring" | "resolved";
type AffectedService = "api" | "auth" | "email" | "pages" | "webhooks" | "analytics";

interface PlatformIncident {
  id: string;
  title: string;
  status: IncidentStatus;
  summary: string;
  affectedServices: AffectedService[];
  updates: Array<{
    id: string;
    status: IncidentStatus;
    message: string;
    createdAt: string;
  }>;
  createdAt: string;
  updatedAt: string;
  resolvedAt: string | null;
}

interface IncidentResponse {
  incidents: PlatformIncident[];
}

const INCIDENT_STATUSES: IncidentStatus[] = ["investigating", "identified", "monitoring", "resolved"];
const STATUS_SERVICES: AffectedService[] = ["api", "auth", "email", "pages", "webhooks", "analytics"];

function HealthSkeleton() {
  return (
    <div className="space-y-6">
      <div>
        <Skeleton className="h-8 w-48" />
        <Skeleton className="mt-2 h-4 w-80" />
      </div>
      <div className="grid gap-4 md:grid-cols-3">
        {Array.from({ length: 3 }).map((_, index) => (
          <SkeletonCard key={index} className="h-24" />
        ))}
      </div>
      <Skeleton className="h-64 w-full rounded-lg" />
    </div>
  );
}

function formatRelativeTime(iso: string | null) {
  if (!iso) return "Unavailable";
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60_000) return "just now";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return `${Math.floor(diff / 86_400_000)}d ago`;
}

const STATUS_STYLES: Record<string, string> = {
  healthy: "border-[var(--color-success)]/50 bg-[var(--color-success)]/5",
  down: "border-[var(--color-destructive)]/50 bg-[var(--color-destructive)]/5",
  unknown: "border-border bg-muted/30",
};

const STATUS_DOT_STYLES: Record<string, string> = {
  healthy: "bg-[var(--color-success)]/50",
  down: "bg-[var(--color-destructive)]/50",
  unknown: "bg-muted-foreground/50",
};

export default function AdminHealthPage() {
  const [health, setHealth] = useState<HealthResponse | null>(null);
  const [incidents, setIncidents] = useState<PlatformIncident[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [incidentError, setIncidentError] = useState<string | null>(null);
  const [isSavingIncident, setIsSavingIncident] = useState(false);
  const [incidentDraft, setIncidentDraft] = useState({
    title: "",
    summary: "",
    status: "investigating" as IncidentStatus,
    affectedServices: ["api"] as AffectedService[],
    updateMessage: "",
  });
  const [incidentEdits, setIncidentEdits] = useState<Record<string, {
    status: IncidentStatus;
    summary: string;
    affectedServices: AffectedService[];
    updateMessage: string;
  }>>({});
  const refreshTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const loadHealth = useCallback(async () => {
    try {
      const response = await apiFetch<HealthResponse>("/api/admin/operations/health");
      setHealth(response);
      setError(null);
    } catch {
      setHealth(null);
      setError("Live worker health is currently unavailable.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadIncidents = useCallback(async () => {
    try {
      const response = await apiFetch<IncidentResponse>("/api/admin/operations/status/incidents");
      setIncidents(response.incidents);
      setIncidentEdits((current) => {
        const next = { ...current };
        for (const incident of response.incidents) {
          next[incident.id] ??= {
            status: incident.status,
            summary: incident.summary,
            affectedServices: incident.affectedServices,
            updateMessage: "",
          };
        }
        return next;
      });
      setIncidentError(null);
    } catch {
      setIncidentError("Incident management is currently unavailable.");
    }
  }, []);

  const refreshAll = useCallback(async () => {
    await Promise.all([loadHealth(), loadIncidents()]);
  }, [loadHealth, loadIncidents]);

  useEffect(() => {
    void refreshAll();
    refreshTimerRef.current = setInterval(() => {
      void refreshAll();
    }, REFRESH_INTERVAL_MS);

    return () => {
      if (refreshTimerRef.current !== null) {
        clearInterval(refreshTimerRef.current);
      }
    };
  }, [refreshAll]);

  const toggleDraftService = (service: AffectedService) => {
    setIncidentDraft((current) => ({
      ...current,
      affectedServices: current.affectedServices.includes(service)
        ? current.affectedServices.filter((entry) => entry !== service)
        : [...current.affectedServices, service],
    }));
  };

  const toggleIncidentService = (incidentId: string, service: AffectedService) => {
    setIncidentEdits((current) => {
      const entry = current[incidentId];
      if (!entry) {
        return current;
      }

      return {
        ...current,
        [incidentId]: {
          ...entry,
          affectedServices: entry.affectedServices.includes(service)
            ? entry.affectedServices.filter((candidate) => candidate !== service)
            : [...entry.affectedServices, service],
        },
      };
    });
  };

  const createIncident = async () => {
    setIsSavingIncident(true);
    try {
      await apiFetch<{ incident: PlatformIncident }>("/api/admin/operations/status/incidents", {
        method: "POST",
        body: JSON.stringify(incidentDraft),
      });
      setIncidentDraft({
        title: "",
        summary: "",
        status: "investigating",
        affectedServices: ["api"],
        updateMessage: "",
      });
      await loadIncidents();
    } catch {
      setIncidentError("Failed to create incident.");
    } finally {
      setIsSavingIncident(false);
    }
  };

  const saveIncident = async (incidentId: string) => {
    const edit = incidentEdits[incidentId];
    if (!edit) {
      return;
    }

    setIsSavingIncident(true);
    try {
      await apiFetch<{ incident: PlatformIncident }>(`/api/admin/operations/status/incidents/${incidentId}`, {
        method: "PATCH",
        body: JSON.stringify(edit),
      });
      setIncidentEdits((current) => ({
        ...current,
        [incidentId]: {
          ...edit,
          updateMessage: "",
        },
      }));
      await loadIncidents();
    } catch {
      setIncidentError("Failed to update incident.");
    } finally {
      setIsSavingIncident(false);
    }
  };

  if (isLoading) {
    return <HealthSkeleton />;
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-white">System Health</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Mounted worker health checks refresh every 30 seconds. Queue and infrastructure detail stay on their dedicated runtime surfaces until those contracts land.
        </p>
      </div>

      {error ? (
        <div className="rounded-lg border border-border bg-[var(--color-background)] p-4">
          <h2 className="text-base font-semibold text-white">Worker health unavailable</h2>
          <p className="mt-2 text-sm text-muted-foreground">{error}</p>
          <button
            onClick={() => {
              setIsLoading(true);
              void refreshAll();
            }}
            className="mt-4 rounded-md border border-border px-4 py-2 text-sm"
          >
            Retry
          </button>
        </div>
      ) : null}

      <section>
        <h2 className="text-lg font-semibold text-white">Mounted Worker Health</h2>
        <div className="mt-4 grid gap-4 md:grid-cols-3">
          <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] p-4">
            <p className="text-xs text-muted-foreground">Healthy workers</p>
            <p className="mt-1 text-2xl font-bold text-white">
              {health ? `${health.summary.healthyCount}/${health.summary.totalWorkers}` : "—"}
            </p>
            <p className="text-xs text-muted-foreground">Real endpoint checks, not stored status rows.</p>
          </div>
          <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] p-4">
            <p className="text-xs text-muted-foreground">Max latency</p>
            <p className="mt-1 text-2xl font-bold text-white">
              {health ? `${health.summary.p95LatencyMs}ms` : "—"}
            </p>
            <p className="text-xs text-muted-foreground">Observed during the latest aggregate refresh.</p>
          </div>
          <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] p-4">
            <p className="text-xs text-muted-foreground">Failed checks</p>
            <p className="mt-1 text-2xl font-bold text-white">{health ? health.summary.errorRate1h : "—"}</p>
            <p className="text-xs text-muted-foreground">Workers returning non-200 or timing out.</p>
          </div>
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-white">Workers</h2>
        {health ? (
          <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {health.workers.map((worker) => (
              <div key={worker.name} className={`rounded-lg border p-4 ${STATUS_STYLES[worker.status] ?? STATUS_STYLES.unknown}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <span className={`h-2 w-2 rounded-full ${STATUS_DOT_STYLES[worker.status] ?? STATUS_DOT_STYLES.unknown}`} />
                    <span className="font-mono text-sm font-medium text-foreground">{worker.name}</span>
                  </div>
                  <span className={`text-xs font-medium uppercase tracking-wide ${worker.status === "healthy" ? "text-[var(--color-success)]" : "text-[var(--color-destructive)]"}`}>
                    {worker.status}
                  </span>
                </div>
                <dl className="mt-3 space-y-1 text-sm text-muted-foreground">
                  <div className="flex justify-between gap-3">
                    <dt>Latency</dt>
                    <dd>{worker.latencyMs}ms</dd>
                  </div>
                  <div className="flex justify-between gap-3">
                    <dt>HTTP</dt>
                    <dd>{worker.httpStatus ?? "No response"}</dd>
                  </div>
                  <div className="flex justify-between gap-3">
                    <dt>Checked</dt>
                    <dd>{formatRelativeTime(worker.lastCheckAt)}</dd>
                  </div>
                </dl>
                <p className="mt-3 truncate text-xs text-muted-foreground">{worker.url}</p>
                {worker.error ? (
                  <p className="mt-2 text-xs text-[var(--color-destructive)]/80">{worker.error}</p>
                ) : null}
              </div>
            ))}
          </div>
        ) : (
          <div className="mt-4 rounded-lg border border-border bg-[var(--color-background)] p-4 text-sm text-muted-foreground">
            Worker health data is unavailable.
          </div>
        )}
      </section>

      <section>
        <h2 className="text-lg font-semibold text-white">Queue and Infrastructure Detail</h2>
        <div className="mt-4 rounded-lg border border-border bg-[var(--color-background)] p-4">
          <p className="text-sm text-muted-foreground">
            This route is now truthful about live worker health only. Queue depth, DLQ counts, and infrastructure component checks remain on the mounted admin operations runtime until their dedicated health contracts are closed.
          </p>
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-white">Published Incidents</h2>
        <div className="mt-4 grid gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]">
          <div className="rounded-lg border border-border bg-[var(--color-background)] p-4">
            <h3 className="text-base font-semibold text-white">Create incident</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Published incidents feed the public status page immediately after save.
            </p>
            <div className="mt-4 space-y-3">
              <input
                value={incidentDraft.title}
                onChange={(event) => setIncidentDraft((current) => ({ ...current, title: event.target.value }))}
                className="w-full rounded-md border border-border bg-transparent px-3 py-2 text-sm"
                placeholder="Incident title"
              />
              <textarea
                value={incidentDraft.summary}
                onChange={(event) => setIncidentDraft((current) => ({ ...current, summary: event.target.value }))}
                className="min-h-24 w-full rounded-md border border-border bg-transparent px-3 py-2 text-sm"
                placeholder="Customer-facing summary"
              />
              <textarea
                value={incidentDraft.updateMessage}
                onChange={(event) => setIncidentDraft((current) => ({ ...current, updateMessage: event.target.value }))}
                className="min-h-20 w-full rounded-md border border-border bg-transparent px-3 py-2 text-sm"
                placeholder="Initial update message"
              />
              <select
                value={incidentDraft.status}
                onChange={(event) => setIncidentDraft((current) => ({ ...current, status: event.target.value as IncidentStatus }))}
                className="w-full rounded-md border border-border bg-transparent px-3 py-2 text-sm"
              >
                {INCIDENT_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
              <div className="flex flex-wrap gap-2">
                {STATUS_SERVICES.map((service) => (
                  <button
                    key={service}
                    type="button"
                    onClick={() => toggleDraftService(service)}
                    className={`rounded-full border px-3 py-1 text-xs uppercase tracking-wide ${incidentDraft.affectedServices.includes(service) ? "border-[var(--color-primary)] text-white" : "border-border text-muted-foreground"}`}
                  >
                    {service}
                  </button>
                ))}
              </div>
              <button
                type="button"
                disabled={
                  isSavingIncident
                  || incidentDraft.title.trim().length < 3
                  || incidentDraft.summary.trim().length < 3
                }
                onClick={() => { void createIncident(); }}
                className="rounded-md bg-white px-4 py-2 text-sm font-medium text-black disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isSavingIncident ? "Saving..." : "Publish incident"}
              </button>
              {incidentError ? (
                <p className="text-sm text-[var(--color-destructive)]">{incidentError}</p>
              ) : null}
            </div>
          </div>

          <div className="space-y-4">
            {incidents.length === 0 ? (
              <div className="rounded-lg border border-border bg-[var(--color-background)] p-4 text-sm text-muted-foreground">
                No incidents have been published yet.
              </div>
            ) : (
              incidents.map((incident) => {
                const edit = incidentEdits[incident.id] ?? {
                  status: incident.status,
                  summary: incident.summary,
                  affectedServices: incident.affectedServices,
                  updateMessage: "",
                };

                return (
                  <article key={incident.id} className="rounded-lg border border-border bg-[var(--color-background)] p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h3 className="text-base font-semibold text-white">{incident.title}</h3>
                        <p className="mt-1 text-xs text-muted-foreground">
                          Created {formatRelativeTime(incident.createdAt)} • Updated {formatRelativeTime(incident.updatedAt)}
                        </p>
                      </div>
                      <select
                        value={edit.status}
                        onChange={(event) => setIncidentEdits((current) => ({
                          ...current,
                          [incident.id]: {
                            ...edit,
                            status: event.target.value as IncidentStatus,
                          },
                        }))}
                        className="rounded-md border border-border bg-transparent px-3 py-2 text-sm"
                      >
                        {INCIDENT_STATUSES.map((status) => (
                          <option key={status} value={status}>
                            {status}
                          </option>
                        ))}
                      </select>
                    </div>

                    <textarea
                      value={edit.summary}
                      onChange={(event) => setIncidentEdits((current) => ({
                        ...current,
                        [incident.id]: {
                          ...edit,
                          summary: event.target.value,
                        },
                      }))}
                      className="mt-3 min-h-20 w-full rounded-md border border-border bg-transparent px-3 py-2 text-sm"
                    />

                    <textarea
                      value={edit.updateMessage}
                      onChange={(event) => setIncidentEdits((current) => ({
                        ...current,
                        [incident.id]: {
                          ...edit,
                          updateMessage: event.target.value,
                        },
                      }))}
                      className="mt-3 min-h-20 w-full rounded-md border border-border bg-transparent px-3 py-2 text-sm"
                      placeholder="Add timeline update"
                    />

                    <div className="mt-3 flex flex-wrap gap-2">
                      {STATUS_SERVICES.map((service) => (
                        <button
                          key={`${incident.id}-${service}`}
                          type="button"
                          onClick={() => toggleIncidentService(incident.id, service)}
                          className={`rounded-full border px-3 py-1 text-xs uppercase tracking-wide ${edit.affectedServices.includes(service) ? "border-[var(--color-primary)] text-white" : "border-border text-muted-foreground"}`}
                        >
                          {service}
                        </button>
                      ))}
                    </div>

                    <div className="mt-4 flex items-center justify-between gap-3">
                      <div className="text-xs text-muted-foreground">
                        {incident.updates.length} published update{incident.updates.length === 1 ? "" : "s"}
                      </div>
                      <button
                        type="button"
                        disabled={isSavingIncident || edit.summary.trim().length < 3}
                        onClick={() => { void saveIncident(incident.id); }}
                        className="rounded-md border border-border px-4 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        Save incident
                      </button>
                    </div>

                    {incident.updates.length > 0 ? (
                      <div className="mt-4 space-y-3 border-t border-border pt-4">
                        {incident.updates.map((update) => (
                          <div key={update.id}>
                            <p className="text-xs uppercase tracking-wide text-muted-foreground">
                              {update.status} • {formatRelativeTime(update.createdAt)}
                            </p>
                            <p className="mt-1 text-sm text-muted-foreground">{update.message}</p>
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </article>
                );
              })
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
